import { type FunctionComponent, type ReactElement, useRef } from 'react';
import { useHover } from 'react-aria';
import { type ImageProps, Popup, type PopupProps } from '@direct-frontend/components';
import { type TestProps } from '@direct-frontend/components/testing';
import { useToggle } from '@direct-frontend/stdlib';
import { type OpenApiSchemaMediaItem, type OpenApiSchemaMediaItemV2 } from '../../api/schema';
import { AdaptiveImage, AdaptiveMedia } from '../AdaptiveImage';
import styles from './MediaZoomPopup.module.css';

export interface MediaZoomPopupProps
    extends TestProps,
        Pick<ImageProps, 'alt' | 'background' | 'height' | 'width' | 'src' | 'borderRadius'> {
    mediaNode: ReactElement;
    placement?: PopupProps['placement'];
    media: OpenApiSchemaMediaItemV2 | OpenApiSchemaMediaItem;
}

export const MediaZoomPopup: FunctionComponent<MediaZoomPopupProps> = ({
    'data-testid': dataTestId = 'MediaZoomPopup',
    mediaNode,
    placement = ['right-start', 'right-end', 'left-start', 'left-end'],
    media,
    ...imageProps
}) => {
    const ref = useRef(null);
    const { value: isOpen, toggleOn: open, toggleOff: close } = useToggle();

    const isMediaItemV2 = 'payload' in media;

    const { hoverProps } = useHover({
        onHoverStart: open,
        onHoverEnd: close,
    });

    // Отключаем автофокус при появлении, чтобы избежать сайд-эффектов при появлении/скрытии других попапов/тултипов
    const autoFocusProps = {
        autoFocus: false,
    };

    return (
        <>
            <div data-testid={dataTestId} className={styles.media} ref={ref} {...hoverProps}>
                {mediaNode}
            </div>
            <Popup
                autoFocusProps={autoFocusProps}
                data-testid={`${dataTestId}.Popup`}
                isOpen={isOpen}
                placement={placement}
                triggerRef={ref}
            >
                {isMediaItemV2 ? (
                    <AdaptiveMedia data-testid={`${dataTestId}.Image`} {...imageProps} fit="contain" media={media} />
                ) : (
                    <AdaptiveImage data-testid={`${dataTestId}.Image`} {...imageProps} fit="contain" media={media} />
                )}
            </Popup>
        </>
    );
};
